import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";

/**
 * CANCELLATION_REASONS — canonical picklist. Keys are stored server-side as
 * `cancellation_reason_key` so we can chart "top cancellation cause" later.
 */
export const CANCELLATION_REASONS = [
  { key: "out_of_stock",     label: "Out of stock" },
  { key: "customer_request", label: "Customer requested" },
  { key: "wrong_address",    label: "Wrong / unreachable address" },
  { key: "courier_failed",   label: "Courier failed / no riders" },
  { key: "payment_issue",    label: "Payment issue" },
  { key: "other",            label: "Other" },
];

export default function CancelOrderModal({ open, order, onClose, onCancelled }) {
  const [reasonKey, setReasonKey] = useState("out_of_stock");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open || !order) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/cancel`, {
        reason_key: reasonKey,
        reason_detail: detail.trim() || null,
      });
      if (data.refunded) {
        toast.success(`Cancelled · refunded ₹${data.refund_amount}`);
      } else {
        toast.success("Order cancelled · customer notified");
      }
      onCancelled?.();
      onClose?.();
      setReasonKey("out_of_stock");
      setDetail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="cancel-order-modal"
      className="fixed inset-0 z-[80] bg-espresso/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose?.(); }}
    >
      <div className="bg-ivory w-full sm:max-w-md border border-line max-h-[92vh] overflow-y-auto">
        <div className="p-5 sm:p-7">
          <div className="label-xs text-rose-600">Cancel order</div>
          <h2 className="mt-2 font-serif text-2xl text-espresso leading-tight">
            Cancel {order.order_number}?
          </h2>
          <p className="mt-2 text-sm text-espresso-light">
            Any courier booking will be cancelled and the customer will be refunded (if paid) and notified by email.
          </p>

          <div className="mt-5">
            <label className="label-xs text-espresso-light block mb-2">Why?</label>
            <div className="relative">
              <select
                value={reasonKey}
                onChange={(e) => setReasonKey(e.target.value)}
                data-testid="cancel-reason-select"
                className="w-full appearance-none px-3 py-3 pr-10 border border-line bg-ivory-100/40 focus:outline-none focus:border-gold text-espresso text-sm cursor-pointer"
              >
                {CANCELLATION_REASONS.map((r) => (
                  <option key={r.key} value={r.key} data-testid={`cancel-reason-${r.key}`}>
                    {r.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-espresso-light pointer-events-none" />
            </div>
          </div>

          <div className="mt-4">
            <label className="label-xs text-espresso-light block mb-2">Notes (optional)</label>
            <textarea
              rows={2}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              maxLength={280}
              data-testid="cancel-reason-detail"
              placeholder="Anything else the customer should know…"
              className="w-full px-3 py-2 border border-line bg-ivory-100/40 focus:outline-none focus:border-gold text-espresso text-sm placeholder:text-espresso-light/70"
            />
            <div className="mt-1 text-right label-xs text-espresso-light">{detail.length}/280</div>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              data-testid="cancel-modal-back"
              className="btn-outline flex-1 disabled:opacity-40"
            >
              Keep order
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="cancel-modal-confirm"
              className="flex-1 px-4 py-3 bg-rose-600 hover:bg-rose-700 text-white text-sm tracking-wider uppercase disabled:opacity-40"
            >
              {submitting ? "Cancelling…" : "Cancel order"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
